import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { FormContainer, Input, PopupContainer } from '../../common/components';
import {
  AUTH_PATTERN,
  AUTH_VALIDATION_CONFIG,
  PasswordErrors
} from '../../auth/forms/validation.pattern';
import { PopupButtons } from '../../common/components/popup';
import { settingsService } from '../service/settings.service';
import { authService } from '../../auth/service/auth.service';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { useThrottle } from '../../hooks/useThrottle';
import { LockInputIcon } from '../../common/components/icons/lock-input-icon.component';

interface IProps {
  onClose: () => void;
  isOpen: boolean;
}

type Inputs = {
  password: string;
  newPassword: string;
  confirmNewPassword: string;
};

export const ChangePassword = ({ isOpen, onClose }: IProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors }
  } = useForm<Inputs>();
  const password = useMutation({
    mutationKey: ['changePassword'],
    mutationFn: async (data: Inputs) => settingsService.changePassword(data),
    onSuccess: async () => {
      onClose();
      toast.success(t('settings.passwordChangedSignOut'));
      await authService.logout();
      setTimeout(() => {
        window.location.href = ROUTER_LINKS.SIGNIN;
      }, 1000);
    },
    onError: (err: any) => {
      if (
        err.response.data.message === 'Password cannot be the same as the old one'
      ) {
        setError('newPassword', {
          message: PasswordErrors.same,
          type: 'manual'
        });
      } else if (err.response.data.message === 'Invalid Password') {
        setError('password', {
          message: PasswordErrors.wrong,
          type: 'manual'
        });
      }
    }
  });
  const throttledPassword = useThrottle(password.mutate);

  const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
    throttledPassword(data);
  };

  return (
    <PopupContainer
      caption={t('settings.changePassword')}
      isOpen={isOpen}
      onClose={onClose}
      description=""
    >
      <FormContainer onSubmit={handleSubmit(onSubmit)} className="mt-[31px]">
        <Input
          errors={errors}
          name="password"
          label={t('settings.currentPassword')}
          placeholder={t('auth.enterHere')}
          type="password"
          icon={<LockInputIcon />}
          register={register.bind(null, 'password', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            }
          })}
        />
        <Input
          errors={errors}
          name="newPassword"
          label={t('settings.newPassword')}
          placeholder={t('auth.enterHere')}
          type="password"
          icon={<LockInputIcon />}
          register={register.bind(null, 'newPassword', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            },
            validate: (value) => value !== watch('password') || PasswordErrors.same
          })}
        />
        <Input
          errors={errors}
          name="confirmNewPassword"
          label={t('settings.confirmPassword')}
          placeholder={t('auth.enterHere')}
          type="password"
          icon={<LockInputIcon />}
          className="w-[345]"
          register={register.bind(null, 'confirmNewPassword', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            },
            validate: (value) =>
              value === watch('newPassword') || PasswordErrors.notMatch
          })}
        />
        <PopupButtons onClose={onClose} text={t('settings.updatePassword')} />
      </FormContainer>
    </PopupContainer>
  );
};
